import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createAction } from '@mews/framework';
import { DistributorPromiseThunkAction } from '../../types';
import { toDateTimeUtc } from '../../utils/dates';
import { getAvailabilityHash } from '../../utils/getAvailabilityHash';
import { getEnterpriseTimezone } from '../entities/enterprises/selectors';
import { RATE_GROUPS_SET } from '../entities/rateGroups/actionTypes';
import { RATES_SET } from '../entities/rates/actionTypes';
import { getPricingHash } from '../pricing/getPricingHash';
import { OccupancyData } from '../resourceOrder/occupancyTypes';
import { fetchServicePricingFailed, fetchServicePricingStarted, fetchServicePricingSucceeded } from './reducers';
import { ServicesGetPricingResponse } from './types';
import { getServicePricingHash } from './utils';

interface FetchServicePricingOptions {
    categoryIds: string[];
    currencyCode: string;
    /** YYYY-MM-DD */
    endDate: string;
    enterpriseId: string;
    languageCode: string;
    occupancyData: Array<OccupancyData>;
    productIds: string[];
    serviceId: string;
    configurationId: string;
    /** YYYY-MM-DD */
    startDate: string;
    availabilityBlockId?: string;
    voucherCode?: string;
    startTimeUtc?: string;
    endTimeUtc?: string;
}

export const fetchServicePricing =
    ({
        availabilityBlockId,
        categoryIds,
        currencyCode,
        endDate,
        enterpriseId,
        languageCode,
        occupancyData,
        productIds,
        serviceId,
        configurationId,
        startDate,
        voucherCode,
        startTimeUtc,
        endTimeUtc,
    }: FetchServicePricingOptions): DistributorPromiseThunkAction<ServicesGetPricingResponse> =>
    async (dispatch, getState, { apiService }) => {
        const state = getState();
        const enterpriseTimeZone = getEnterpriseTimezone(state, { enterpriseId });

        if (enterpriseTimeZone == null) {
            throw new TypeError('Enterprise timezone is null');
        }

        const startUtc = toDateTimeUtc(startDate, enterpriseTimeZone);
        const endUtc = toDateTimeUtc(endDate, enterpriseTimeZone);

        const stringEndUtc = endUtc.value;
        const stringStartUtc = startUtc.value;
        const servicePricingHash = getServicePricingHash({
            currencyCode,
            endUtc: endTimeUtc ?? stringEndUtc,
            enterpriseId,
            occupancyData,
            serviceId,
            startUtc: startTimeUtc ?? stringStartUtc,
            voucherCode,
        });

        const pricingHashes = pipe(
            categoryIds,
            array.map((categoryId): [string, string] => [
                categoryId,
                getPricingHash({
                    startDate: startTimeUtc ?? startDate,
                    endDate: endTimeUtc ?? endDate,
                    enterpriseId,
                    occupancyData,
                    voucherCode,
                    categoryId,
                    productIds,
                }),
            ]),
            (entries) => Object.fromEntries<string>(entries)
        );

        try {
            dispatch(fetchServicePricingStarted({ servicePricingHash, pricingHashes }));
            const response: ServicesGetPricingResponse = await apiService.getServicesPricing({
                availabilityBlockId,
                categoryIds,
                currencyCode,
                endUtc: endTimeUtc ?? stringEndUtc,
                enterpriseId,
                fullAmounts: false,
                languageCode,
                occupancyData,
                productIds,
                serviceId,
                configurationId,
                startUtc: startTimeUtc ?? stringStartUtc,
                voucherCode,
            });

            const availabilityHash = getAvailabilityHash({
                startDate: startTimeUtc ?? startDate,
                endDate: endTimeUtc ?? endDate,
                enterpriseId,
                voucherCode,
            });

            const { categoryPrices, rateGroups, rates, violatedRestrictions, cancellationFees } = response;
            dispatch(
                fetchServicePricingSucceeded({
                    categoryPrices,
                    servicePricingHash,
                    violatedRestrictions,
                    pricingHashes,
                    availabilityHash,
                    cancellationFees,
                    startDate,
                    endDate,
                })
            );
            dispatch(createAction(RATE_GROUPS_SET, { rateGroups, enterpriseId }));
            dispatch(createAction(RATES_SET, { rates, enterpriseId }));
            return response;
        } catch (error) {
            dispatch(fetchServicePricingFailed({ servicePricingHash, pricingHashes }));
            throw error;
        }
    };
